import type { ScenarioWorkspaceItemPanel } from '../../model/types'

import { Fragment, useCallback, useMemo, useState } from 'react'
import {
	ChevronsUpDownIcon,
	LoaderCircleIcon,
	PenIcon,
	XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import CheckWaveIcon from '@/shared/components/icons/CheckWaveIcon'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseAdornmentButton,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Tag } from '@/shared/components/ui/tag'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'

import { usePanelController, useScenarios } from '../ScenariosProvider'

export const ScenarioToolbar = ({
	panel: { id },
	className,
}: {
	panel: ScenarioWorkspaceItemPanel
	className?: string
}) => {
	const { t } = useTranslation()

	const { scenarios, isLoading } = useScenarios()
	const { updatePanel, panel, removePanel } = usePanelController(id)
	const [open, setOpen] = useState(false)
	const { showModal } = useModals()
	const selectedScenario = useMemo(
		() => scenarios?.find(({ id }) => id === panel?.scenarioId),
		[scenarios, panel?.scenarioId]
	)
	const handleOpenEditScenarioModal = useCallback(() => {
		ampli.scenarioPanelEditClick()
		showModal('EDIT_SCENARIO', { scenarioId: panel!.scenarioId! })
	}, [panel?.scenarioId, showModal])
	const handleRemoveScenario = useCallback(() => {
		ampli.scenarioPanelRemoveClick()
		removePanel()
	}, [removePanel])
	const handleSelectScenario = useCallback(
		(scenarioId: string) => {
			ampli.scenarioPanelSelectScenario()
			updatePanel({ scenarioId })
			setOpen(false)
		},
		[updatePanel]
	)
	const isOnlyRead = !selectedScenario || selectedScenario?.is_main

	return (
		<InputBase role='combobox' className={cn('w-full', className)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					data-testid='scenario-toolbar-trigger'
					className='flex min-w-0 flex-1 cursor-pointer items-center justify-between'
				>
					<InputBaseFlexWrapper
						className={cn(
							'min-w-0 gap-2',
							!panel?.scenarioId && 'text-placeholder'
						)}
					>
						{selectedScenario ? (
							<Fragment>
								<div className='flex w-full min-w-0 flex-1 place-items-center gap-2'>
									<Tooltip>
										<TooltipTrigger asChild>
											<span className='block truncate text-left'>
												<b className='font-semibold'>
													{selectedScenario.optimization_group_name}
												</b>
												<span className='mx-1 text-muted-foreground'>-</span>
												{selectedScenario.name}
											</span>
										</TooltipTrigger>
										<TooltipContent>{`${selectedScenario.optimization_group_name} - ${selectedScenario.name}`}</TooltipContent>
									</Tooltip>
									{selectedScenario.is_main && (
										<Tooltip>
											<TooltipTrigger asChild>
												<CheckWaveIcon className='shrink-0' />
											</TooltipTrigger>
											<TooltipContent>{t('app.live')}</TooltipContent>
										</Tooltip>
									)}
								</div>
								{selectedScenario.scenario_type === 'recommendation' && (
									<Tag variant='epic' size='sm' className='mr-2'>
										{t('app.ai')}
									</Tag>
								)}
							</Fragment>
						) : (
							<span className='truncate'>
								{t('scenarios.select_scenario.placeholder')}
							</span>
						)}
					</InputBaseFlexWrapper>
					<InputBaseAdornment>
						<ChevronsUpDownIcon className='size-4 text-muted-foreground opacity-50' />
					</InputBaseAdornment>
				</PopoverTrigger>
				<PopoverContent className='min-w-(--radix-popover-trigger-width) p-0'>
					<Command>
						<CommandInput placeholder={t('search.scenarios.placeholder')} />
						<Virtualized asChild>
							<CommandList>
								{!isLoading && <CommandEmpty>{t('no_scenarios')}</CommandEmpty>}
								{isLoading ? (
									<div className='flex items-center justify-center py-4'>
										<LoaderCircleIcon className='size-4 animate-spin opacity-50' />
									</div>
								) : (
									<CommandGroup>
										<VirtualizedVirtualizer>
											{scenarios?.map(
												({ optimization_group_name, name, id, is_main }) => (
													<CommandItem
														data-value={id}
														value={id}
														key={id}
														keywords={[name, optimization_group_name]}
														onSelect={() => handleSelectScenario(id)}
													>
														{`${optimization_group_name} - ${name}`}
														{is_main && <CheckWaveIcon />}
													</CommandItem>
												)
											)}
										</VirtualizedVirtualizer>
									</CommandGroup>
								)}
							</CommandList>
						</Virtualized>
					</Command>
				</PopoverContent>
			</Popover>
			<Tooltip>
				<TooltipTrigger asChild>
					<InputBaseAdornmentButton
						data-testid='scenario-toolbar-edit'
						onClick={handleOpenEditScenarioModal}
						disabled={isOnlyRead}
					>
						<PenIcon className='size-4 text-muted-foreground' />
					</InputBaseAdornmentButton>
				</TooltipTrigger>
				<TooltipContent>
					{isOnlyRead
						? t('scenario.read_only.text')
						: t('scenario.edit.tooltip')}
				</TooltipContent>
			</Tooltip>
			<Tooltip>
				<TooltipTrigger asChild>
					<InputBaseAdornmentButton
						data-testid='scenario-toolbar-remove'
						onClick={handleRemoveScenario}
					>
						<XIcon className='size-4 text-muted-foreground' />
					</InputBaseAdornmentButton>
				</TooltipTrigger>
				<TooltipContent>{t('scenario.close.tooltip')}</TooltipContent>
			</Tooltip>
		</InputBase>
	)
}
